/// <reference types="vite/client" />

import { decodeJwtPayload } from './jwt';

/**
 * Simple cookie utilities for cross-subdomain auth sharing.
 *
 * The hub writes these cookies; other microsites (quality, etc.) read them.
 * No Supabase dependency — these are plain JWT + partner-key cookies.
 *
 * Set the VITE_COOKIE_SUFFIX env var (e.g. "_staging") to append a suffix
 * to cookie names, avoiding clashes when multiple environments share a domain.
 */

declare global {
  interface ImportMetaEnv {
    readonly VITE_COOKIE_SUFFIX?: string;
  }
}

const cookieSuffix: string = import.meta.env.VITE_COOKIE_SUFFIX ?? '';

export const AUTH_COOKIE = `artic_token${cookieSuffix}`;
export const REFRESH_COOKIE = `artic_refresh_token${cookieSuffix}`;
export const AUTH_SESSION_COOKIE = `artic_auth_session${cookieSuffix}`;
export const AUTH_REFRESH_ATTEMPT_COOKIE_PREFIX = `artic_auth_refresh_attempt${cookieSuffix}_`;
export const AUTH_LOGOUT_INTENT_COOKIE = `artic_auth_logout${cookieSuffix}`;
export const AUTH_LOGOUT_GENERATION_COOKIE = `artic_auth_logout_generation${cookieSuffix}`;
export const PARTNER_COOKIE = `artic_partner${cookieSuffix}`;
/** The last non-Artic partner an admin viewed — pinned in the Change Partner switcher. */
export const LAST_PARTNER_COOKIE = `artic_last_partner${cookieSuffix}`;

export interface AuthCookieOptions {
  /** Cookie domain (e.g. `.artic.works`). Omit for localhost / same-origin. */
  domain?: string;
}

export interface AuthSessionCookies {
  accessToken: string;
  refreshToken: string;
  previousRefreshToken: string;
}

export interface AuthRefreshAttempt {
  cookieName: string;
  refreshToken: string;
  logoutGeneration: string | undefined;
}

/** ~400 days — matches the dual-storage session cookie max-age. */
const MAX_AGE = 34_560_000;
const REFRESH_ATTEMPT_MAX_AGE = 30;

function isSecureContext(): boolean {
  return typeof location !== 'undefined' && location.protocol === 'https:';
}

function buildAttrs(maxAge: number, domain?: string): string {
  let attrs = `; path=/; max-age=${String(maxAge)}; SameSite=Lax`;
  if (domain) attrs += `; domain=${domain}`;
  if (isSecureContext()) attrs += '; Secure';
  return attrs;
}

/**
 * Expire any host-only shadow of `name`. A cookie set without a `domain`
 * attribute is stored host-only on the current hostname; that shadow
 * survives every domain-scoped write and wins `document.cookie` reads
 * (most-specific match), keeping a stale value alive past every refresh.
 * Sentry ARTIC-HUB-PZ — see DoctorPage.tsx `articCookieShadows`.
 */
function expireHostOnlyShadow(name: string): void {
  document.cookie = `${name}=${buildAttrs(0)}`;
}

// ── Writers ─────────────────────────────────────────────────────────

export function setAuthTokenCookie(token: string, options: AuthCookieOptions = {}): void {
  if (options.domain) expireHostOnlyShadow(AUTH_COOKIE);
  document.cookie = `${AUTH_COOKIE}=${encodeURIComponent(token)}${buildAttrs(MAX_AGE, options.domain)}`;
}

export function setRefreshTokenCookie(token: string, options: AuthCookieOptions = {}): void {
  if (options.domain) expireHostOnlyShadow(REFRESH_COOKIE);
  document.cookie = `${REFRESH_COOKIE}=${encodeURIComponent(token)}${buildAttrs(MAX_AGE, options.domain)}`;
}

function accessTokenFingerprint(token: string): string {
  const parts = token.split('.');
  return parts.length === 3 && parts[2] ? parts[2] : token;
}

function uniqueAuthMarker(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${String(Date.now())}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Write the projection cookies followed by a small commit cookie. Consumers
 * that need both tokens can reject a snapshot taken between the individual
 * cookie writes by checking the committed access-token fingerprint.
 */
export function setAuthSessionCookies(
  accessToken: string,
  refreshToken: string,
  options: AuthCookieOptions = {},
  previousRefreshToken?: string,
): void {
  const currentSession = readAuthSessionCookies();
  const isIdempotentWrite =
    currentSession?.accessToken === accessToken && currentSession.refreshToken === refreshToken;
  const replacedRefreshToken =
    previousRefreshToken ??
    (isIdempotentWrite ? currentSession.previousRefreshToken : refreshToken);
  setAuthTokenCookie(accessToken, options);
  setRefreshTokenCookie(refreshToken, options);

  if (options.domain) expireHostOnlyShadow(AUTH_SESSION_COOKIE);
  const commit = JSON.stringify({
    version: 2,
    accessTokenFingerprint: accessTokenFingerprint(accessToken),
    refreshToken,
    previousRefreshToken: replacedRefreshToken,
  });
  document.cookie = `${AUTH_SESSION_COOKIE}=${encodeURIComponent(commit)}${buildAttrs(MAX_AGE, options.domain)}`;
  // The committed pair is the winner. Attempts using its previous refresh
  // token are race losers; attempts already using this same token are still
  // live and must remain visible to other contexts.
  clearSupersededAuthRefreshAttempts(refreshToken, options);
}

export function beginAuthRefreshAttempt(
  refreshToken: string,
  options: AuthCookieOptions = {},
): AuthRefreshAttempt {
  const owner = uniqueAuthMarker();
  const cookieName = `${AUTH_REFRESH_ATTEMPT_COOKIE_PREFIX}${owner}`;
  const logoutGeneration = readCookie(AUTH_LOGOUT_GENERATION_COOKIE);
  const value = JSON.stringify({ refreshToken, logoutGeneration });
  document.cookie = `${cookieName}=${encodeURIComponent(value)}${buildAttrs(REFRESH_ATTEMPT_MAX_AGE, options.domain)}`;
  return { cookieName, refreshToken, logoutGeneration };
}

export function beginAuthLogout(options: AuthCookieOptions = {}): void {
  const generation = uniqueAuthMarker();
  if (options.domain) {
    expireHostOnlyShadow(AUTH_LOGOUT_INTENT_COOKIE);
    expireHostOnlyShadow(AUTH_LOGOUT_GENERATION_COOKIE);
  }
  document.cookie = `${AUTH_LOGOUT_GENERATION_COOKIE}=${generation}${buildAttrs(MAX_AGE, options.domain)}`;
  document.cookie = `${AUTH_LOGOUT_INTENT_COOKIE}=${generation}${buildAttrs(MAX_AGE, options.domain)}`;
}

export function clearAuthLogoutIntent(options: AuthCookieOptions = {}): void {
  document.cookie = `${AUTH_LOGOUT_INTENT_COOKIE}=${buildAttrs(0, options.domain)}`;
  if (options.domain) expireHostOnlyShadow(AUTH_LOGOUT_INTENT_COOKIE);
}

export function endAuthRefreshAttempt(
  attempt: AuthRefreshAttempt,
  options: AuthCookieOptions = {},
): void {
  document.cookie = `${attempt.cookieName}=${buildAttrs(0, options.domain)}`;
  if (options.domain) expireHostOnlyShadow(attempt.cookieName);
}

export function setPartnerCookie(partnerKey: string, options: AuthCookieOptions = {}): void {
  if (options.domain) expireHostOnlyShadow(PARTNER_COOKIE);
  document.cookie = `${PARTNER_COOKIE}=${encodeURIComponent(partnerKey)}${buildAttrs(MAX_AGE, options.domain)}`;
}

/**
 * Remember the last non-Artic partner an admin viewed so the Change Partner
 * switcher can pin it. Shared across subdomains, exactly like the active-partner
 * cookie.
 */
export function setLastPartnerCookie(partnerKey: string, options: AuthCookieOptions = {}): void {
  if (options.domain) expireHostOnlyShadow(LAST_PARTNER_COOKIE);
  document.cookie = `${LAST_PARTNER_COOKIE}=${encodeURIComponent(partnerKey)}${buildAttrs(MAX_AGE, options.domain)}`;
}

export function clearAuthCookies(options: AuthCookieOptions = {}): void {
  const expire = buildAttrs(0, options.domain);
  document.cookie = `${AUTH_COOKIE}=${expire}`;
  document.cookie = `${REFRESH_COOKIE}=${expire}`;
  document.cookie = `${AUTH_SESSION_COOKIE}=${expire}`;
  document.cookie = `${PARTNER_COOKIE}=${expire}`;
  document.cookie = `${LAST_PARTNER_COOKIE}=${expire}`;
  if (options.domain) {
    expireHostOnlyShadow(AUTH_COOKIE);
    expireHostOnlyShadow(REFRESH_COOKIE);
    expireHostOnlyShadow(AUTH_SESSION_COOKIE);
    expireHostOnlyShadow(PARTNER_COOKIE);
    expireHostOnlyShadow(LAST_PARTNER_COOKIE);
  }
  clearAuthRefreshAttempts(options);
}

// ── Reader (any microsite) ──────────────────────────────────────────

/** Read a single cookie value by name. Returns `undefined` if absent. */
function readCookieFromSnapshot(snapshot: string, name: string): string | undefined {
  const match = new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`).exec(
    snapshot,
  );
  if (match?.[1] === undefined) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
}

function authRefreshAttemptCookieNames(snapshot: string): string[] {
  return snapshot
    .split(';')
    .map((pair) => pair.split('=')[0]?.trim())
    .filter((name): name is string =>
      Boolean(name?.startsWith(AUTH_REFRESH_ATTEMPT_COOKIE_PREFIX)),
    );
}

function readAuthRefreshAttempts(): AuthRefreshAttempt[] {
  if (typeof document === 'undefined') return [];
  const snapshot = document.cookie;
  const attempts: AuthRefreshAttempt[] = [];
  for (const cookieName of authRefreshAttemptCookieNames(snapshot)) {
    const value = readCookieFromSnapshot(snapshot, cookieName);
    if (!value) continue;
    try {
      const parsed = JSON.parse(value) as Record<string, unknown>;
      if (typeof parsed['refreshToken'] === 'string') {
        attempts.push({
          cookieName,
          refreshToken: parsed['refreshToken'],
          logoutGeneration:
            typeof parsed['logoutGeneration'] === 'string' ? parsed['logoutGeneration'] : undefined,
        });
      }
    } catch {
      // Ignore malformed/expired attempt markers.
    }
  }
  return attempts;
}

function clearAuthRefreshAttempts(options: AuthCookieOptions): void {
  if (typeof document === 'undefined') return;
  for (const cookieName of authRefreshAttemptCookieNames(document.cookie)) {
    document.cookie = `${cookieName}=${buildAttrs(0, options.domain)}`;
    if (options.domain) expireHostOnlyShadow(cookieName);
  }
}

function clearSupersededAuthRefreshAttempts(
  committedRefreshToken: string,
  options: AuthCookieOptions,
): void {
  for (const attempt of readAuthRefreshAttempts()) {
    if (attempt.refreshToken === committedRefreshToken) continue;
    endAuthRefreshAttempt(attempt, options);
  }
}

export function hasAuthRefreshAttempt(): boolean {
  return readAuthRefreshAttempts().length > 0;
}

export function isAuthRefreshAttemptActive(attempt: AuthRefreshAttempt): boolean {
  return readAuthRefreshAttempts().some(
    (current) =>
      current.cookieName === attempt.cookieName && current.refreshToken === attempt.refreshToken,
  );
}

export function isAuthRefreshAttemptFromCurrentGeneration(attempt: AuthRefreshAttempt): boolean {
  return attempt.logoutGeneration === readCookie(AUTH_LOGOUT_GENERATION_COOKIE);
}

export function hasAuthLogoutIntent(): boolean {
  return readCookie(AUTH_LOGOUT_INTENT_COOKIE) !== undefined;
}

export function hasOtherAuthRefreshAttempt(
  refreshToken: string,
  ownAttempt: AuthRefreshAttempt,
): boolean {
  return readAuthRefreshAttempts().some(
    (attempt) =>
      attempt.cookieName !== ownAttempt.cookieName && attempt.refreshToken === refreshToken,
  );
}

export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return readCookieFromSnapshot(document.cookie, name);
}

export function readJwtSubject(token: string | undefined): string | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  return payload && typeof payload['sub'] === 'string' ? payload['sub'] : null;
}

export function isSameJwtUser(first: string | undefined, second: string): boolean {
  const firstSubject = readJwtSubject(first);
  return firstSubject !== null && firstSubject === readJwtSubject(second);
}

/** Read an access/refresh pair only when its final commit cookie matches. */
export function readAuthSessionCookies(): AuthSessionCookies | undefined {
  if (typeof document === 'undefined') return undefined;

  const snapshot = document.cookie;
  const accessToken = readCookieFromSnapshot(snapshot, AUTH_COOKIE);
  const serializedCommit = readCookieFromSnapshot(snapshot, AUTH_SESSION_COOKIE);
  if (!accessToken || !serializedCommit) return undefined;

  try {
    const commit = JSON.parse(serializedCommit) as Record<string, unknown>;
    if (
      commit['version'] !== 2 ||
      typeof commit['accessTokenFingerprint'] !== 'string' ||
      typeof commit['refreshToken'] !== 'string' ||
      typeof commit['previousRefreshToken'] !== 'string' ||
      commit['refreshToken'].length === 0 ||
      commit['previousRefreshToken'].length === 0 ||
      commit['accessTokenFingerprint'] !== accessTokenFingerprint(accessToken)
    ) {
      return undefined;
    }
    return {
      accessToken,
      refreshToken: commit['refreshToken'],
      previousRefreshToken: commit['previousRefreshToken'],
    };
  } catch {
    return undefined;
  }
}
